import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { IAxiosResponseWithPagination } from 'types/entities'
import { IMemberConferenceParams } from './MemberConference.interface'
import { ENDPOINTS } from 'const/endpoints'
import axios from 'axios'
import { getMemberConference, getMemberConferenceDropdown } from './MemberConference.service'
import { useUrlQuery } from 'hooks/url'
import { useParams } from 'react-router-dom'
import { getClientDetails, useSortHandler } from 'hooks/common'
import { onGetTablebodyColumn, onGetTableColumns } from 'utils/common'
import { useMemo } from 'react'
import { Column } from 'react-table'
import { formatDate, SLOT_TIME_FORMAT } from 'utils/time'
import { CONFERENCE_STATUS } from 'modules/ConferenceMeeting/Conferences.constants'
import { NAMES } from 'modules/ConferenceMeeting/MeetingInformation/ConferenceInformation/ConferenceProviders/ConferenceProviders.constants'

export function useMemberConferenceTableData(
	tableSectionDetail: any,
): [
	Column<{ [key: string]: React.ReactNode }>[],
	{ [key: string]: React.ReactNode }[],
	(param: string) => void,
	any,
] {
	const [params] = useUrlQuery<IMemberConferenceParams>()
	const param = useParams<{ id: string }>()
	const conferenceID = params?.conference
	const memberConference = useMemberConference({
		...params,
		...param,
		clientId: getClientDetails()?.clientId,
		isEnabled: !!conferenceID,
	})

	const onSort = useSortHandler(params.sort)
	const dynamicColumn = tableSectionDetail?.providerColumns
	const isMembersConferenceTableColumn = onGetTableColumns(dynamicColumn)
	const memberProfileConference = useMemo(() => {
		const mapBodyObj = (item: any) => {
			const [conferenceAttendes] = item?.conferenceAttendes
			return {
				status: (
					<div className="max-w-[400px] w-full space-y-1">
						{item?.conferenceAttendes?.length > 0 ? (
							item.conferenceAttendes.map((attendee: any, index: number) => (
								<div key={index}>{attendee?.status?.globalCodesName || NAMES?.NOT_JOINED}</div>
							))
						) : (
							<div>{NAMES?.NOT_JOINED}</div>
						)}
					</div>
				),
				date: item?.date ? formatDate(+item?.date) : '--',
				joinTime: conferenceAttendes?.joinTime
					? formatDate(+conferenceAttendes?.joinTime, SLOT_TIME_FORMAT)
					: '--',
				leaveTime: conferenceAttendes?.leaveTime
					? formatDate(+conferenceAttendes?.leaveTime, SLOT_TIME_FORMAT)
					: '--',
				startTime: item?.startTime ? formatDate(+item?.startTime, SLOT_TIME_FORMAT) : '--',
				endTime: item?.endTime ? formatDate(+item?.endTime, SLOT_TIME_FORMAT) : '--',
				duration: conferenceAttendes?.duration ? `${conferenceAttendes?.duration} minutes` : '--',
			}
		}
		return (
			memberConference?.data?.data?.items?.map((item: any) => {
				const bodyobj = mapBodyObj(item)
				return onGetTablebodyColumn(item, bodyobj, isMembersConferenceTableColumn)
			}, []) || []
		)
	}, [memberConference?.data?.data?.items, isMembersConferenceTableColumn])
	return [isMembersConferenceTableColumn, memberProfileConference, onSort, memberConference]
}

interface IMemberVitalAlertsQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<any>>>,
		Partial<IMemberConferenceParams> {
	isEnabled?: boolean
}
export function useMemberConference({
	limit,
	page,
	sort,
	search,
	isEnabled = true,
	id,
	status,
	takenAt,
	type,
	conference,
	...rest
}: IMemberVitalAlertsQueryProps = {}) {
	return useQuery<
		IAxiosResponseWithPagination<Partial<any>>,
		any,
		IAxiosResponseWithPagination<Partial<any>>
	>(
		[ENDPOINTS.MEMBER_CONFERENCE, sort, limit, id, page, conference, search, status, takenAt, type],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getMemberConference(
				id,
				{
					page,
					sort,
					limit,
					search,
					status,
					takenAt,
					type,
					conference,
				},
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			suspense: false,
			...rest,
		},
	)
}

export interface IMemberConference {
	topic: string
	id: string
}

export interface IMemberConferenceDropdownParams {
	page: number
	sort: string
	limit: number
	id: string
	search: string
}

interface IMemberConferenceDropdownQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<IMemberConference>>,
		Partial<IMemberConferenceDropdownParams> {
	isEnabled?: boolean
}
export function useMemberConferenceFilterDropdown({
	id = '',
	isEnabled = true,
	search,
	page,
	limit,
	...rest
}: IMemberConferenceDropdownQueryProps) {
	return useQuery<
		IAxiosResponseWithPagination<IMemberConference>,
		any,
		IAxiosResponseWithPagination<IMemberConference>
	>(
		[ENDPOINTS.MEMBER_DROPDOWN_CONFERENCE, id, search, page, limit],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getMemberConferenceDropdown(id, { page, limit, search }, source)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}
